import React from 'react';
import { useSelector } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form/immutable';
import { Button, Grid, Typography } from '@material-ui/core';

import Box from 'retired/Box';
import FormFileUpload from 'retired/FormFileUpload';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormTextField from 'retired/FormTextField';
import FormCheckbox from 'retired/FormCheckbox';
import { ViewInAppButton } from 'components/ViewInAppButton';
import { selectOrganization } from 'entities/user/selectors';
import { getAppDomain } from 'utils/getAppDomain';

import { HomepageBannerData } from './actions';
import { HOMEPAGE_BANNER_FORM_IDENTIFIER as FORM_IDENTIFIER } from './constants';

const withForm = reduxForm<HomepageBannerData>({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

const valueSelector = formValueSelector(FORM_IDENTIFIER);

interface HomepageBannerProps {
  submitting: boolean;
  invalid: boolean;
  pristine: boolean;
  handleSubmit: (
    event: (data: { toJS(): HomepageBannerData }) => void
  ) => ((event: React.FormEvent<HTMLFormElement>) => void) | undefined;
  submitForm: (data: HomepageBannerData) => void;
}

const HomepageBannerForm = withForm(
  ({ submitting, handleSubmit, submitForm }: HomepageBannerProps): React.ReactElement => {
    const hideHomepageTagline = useSelector((state) => valueSelector(state, 'hideHomepageTagline'));
    const organization = useSelector(selectOrganization);
    return (
      <form onSubmit={handleSubmit((data) => submitForm(data.toJS()))}>
        <fieldset>
          <Box style={{ paddingTop: 0, marginBottom: 30 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <h2>Homepage Banner</h2>
              <ViewInAppButton
                href={getAppDomain([organization])}
                iconSize={18}
                style={{
                  display: 'inline-block',
                  marginRight: 30,
                  marginTop: 25,
                }}
              />
            </div>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={8} xl={9}>
                <FormFieldLabel forName="homepageBanner">Banner Image (optional)</FormFieldLabel>
                <Field
                  id="homepageBanner"
                  name="homepageBanner"
                  component={FormFileUpload}
                  labelText="Upload an image for your homepage banner. Make sure that the image format is a JPG."
                  acceptedTypes="image/jpeg"
                  maxSize={1024 * 1024 * 10}
                  imagePreview
                  withCrop
                  cropRatio={1440 / 436}
                  fixed
                  allowClear
                />
              </Grid>
              <Grid item xs={12} lg={4} xl={3}>
                <FormFieldLabel
                  forName="homepageTagline"
                  style={{ opacity: hideHomepageTagline ? 0.38 : 1 }}
                >
                  Banner Tagline (optional)
                </FormFieldLabel>
                <Field
                  id="homepageTagline"
                  name="homepageTagline"
                  component={FormTextField}
                  inputProps={{ maxLength: 50 }}
                  multiline
                  fullWidth
                  label="Max 50 Characters."
                  helperText={`Alternative to "Let's do something good today."`}
                  disabled={hideHomepageTagline}
                />
                <Field
                  id="hideHomepageTagline"
                  name="hideHomepageTagline"
                  label="Hide banner tagline"
                  component={FormCheckbox}
                />
                <div style={{ width: '100%' }} />
                <Field
                  id="homepageGradient"
                  name="homepageGradient"
                  component={FormCheckbox}
                  label="Add black gradient overlay (recommended)"
                />
              </Grid>
            </Grid>

            <Typography align="right">
              <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                Submit
              </Button>
            </Typography>
          </Box>
        </fieldset>
      </form>
    );
  }
);

export default HomepageBannerForm;
